import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { X, CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';

// Phase 1 section keys - MUST match OnboardingContext SECTION_KEYS and OnboardingHub sections_completed keys
const SECTION_CONFIG = [
  { key: 'business_profile', label: 'Business Profile' },
  { key: 'bank_accounts', label: 'Bank Accounts' },
  { key: 'inventory_list_setup', label: 'Inventory List Setup' },
  { key: 'vendors', label: 'Vendor Setup' },
  { key: 'prep_menu_recipes', label: 'Prep & Menu Recipes' },
  { key: 'pos_integration', label: 'POS Integration' },
  { key: 'payroll', label: 'Payroll Setup' },
] as const;

const PHASE2_SECTION_CONFIG = [
  { key: 'bank_accounts_phase2', label: 'Bank Accounts Training' },
  { key: 'cogs_vendor_training', label: 'COGS Vendor Training' },
  { key: 'pos_reports', label: 'POS Reports' },
] as const;

function isPhase1SectionDone(
  sectionsCompleted: Record<string, unknown>,
  key: string
): boolean {
  const section = sectionsCompleted[key] as { status?: string } | undefined;
  return !!(section && (section.status === 'completed' || section.status === 'skipped'));
}

export const OnboardingStatusCard = () => {
  const navigate = useNavigate();
  const { data: progressData, isLoading: progressLoading } = useOnboardingProgress();
  const [isDismissed, setIsDismissed] = useState(false);

  const sectionsCompleted = (progressData?.sections_completed ?? {}) as Record<string, unknown>;
  const phase2Data = (sectionsCompleted.phase2 as Record<string, { status?: string }>) ?? {};

  const phase1Complete = useMemo(() => {
    if (!progressData) return false;
    const marked = !!progressData.completed_at;
    if (marked) return true;
    return SECTION_CONFIG.every(({ key }) => isPhase1SectionDone(sectionsCompleted, key));
  }, [progressData, sectionsCompleted]);

  const phase2Sections = phase2Data;

  const phase1CompletedCount = useMemo(
    () =>
      SECTION_CONFIG.filter(({ key }) => isPhase1SectionDone(sectionsCompleted, key)).length,
    [sectionsCompleted]
  );
  const phase1Percent =
    SECTION_CONFIG.length > 0
      ? Math.round((phase1CompletedCount / SECTION_CONFIG.length) * 100)
      : 0;

  const phase2CompletedCount = useMemo(
    () =>
      PHASE2_SECTION_CONFIG.filter(
        ({ key }) => phase2Sections[key]?.status === 'completed'
      ).length,
    [phase2Sections]
  );
  const phase2Percent =
    PHASE2_SECTION_CONFIG.length > 0
      ? Math.round((phase2CompletedCount / PHASE2_SECTION_CONFIG.length) * 100)
      : 0;

  const isSectionCompleted = useMemo(
    () => (key: string) => isPhase1SectionDone(sectionsCompleted, key),
    [sectionsCompleted]
  );

  // Dismissal: clear on visibility change; respect sessionStorage per phase
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sessionStorage.removeItem('onboarding-status-dismissed');
        sessionStorage.removeItem('onboarding-phase2-status-dismissed');
        setIsDismissed(false);
      }
    };
    sessionStorage.removeItem('onboarding-status-dismissed');
    sessionStorage.removeItem('onboarding-phase2-status-dismissed');
    setIsDismissed(false);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (progressLoading) return;
    const key = phase1Complete ? 'onboarding-phase2-status-dismissed' : 'onboarding-status-dismissed';
    setIsDismissed(sessionStorage.getItem(key) === 'true');
  }, [progressLoading, phase1Complete]);

  const handleDismiss = () => {
    setIsDismissed(true);
    const key = phase1Complete ? 'onboarding-phase2-status-dismissed' : 'onboarding-status-dismissed';
    sessionStorage.setItem(key, 'true');
  };

  const handleContinue = () => navigate('/onboarding');

  if (progressLoading) {
    return (
      <div className="mb-6">
        <Card className="border-primary/20 shadow-lg">
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-2 w-full" />
              <div className="flex flex-wrap gap-2">
                {SECTION_CONFIG.map(({ key }) => (
                  <Skeleton key={key} className="h-8 w-24 rounded-full" />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Auto-hide when Phase 1 is 100% (7/7 sections) so user sees Action Needed / notifications
  if (isDismissed || phase1Complete) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="mb-6"
    >
      <Card className="bg-gradient-to-br from-primary/5 via-background to-accent/5 border-primary/20 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    📋 Complete Your Setup
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    You're <span className="font-semibold text-foreground">{phase1Percent}%</span> done!
                    Complete your onboarding to unlock the full power of Dollar Bill
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={handleDismiss} className="h-8 w-8 rounded-full">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {phase1CompletedCount} of {SECTION_CONFIG.length} sections completed
                  </span>
                  <span className="font-medium">{phase1Percent}%</span>
                </div>
                <Progress value={phase1Percent} className="h-2" />
              </div>
              <div className="flex flex-wrap gap-2">
                {SECTION_CONFIG.map(({ key, label }) => {
                  const isCompleted = isSectionCompleted(key);
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/50 border text-xs"
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <span className={isCompleted ? 'text-foreground' : 'text-muted-foreground'}>
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div>
                <Button onClick={handleContinue} className="gap-2">
                  {phase1Percent === 0 ? 'Start Onboarding' : 'Continue Onboarding'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
